import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AIService } from './ai.service';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  type?: string;
  sourceDocuments?: string[];
  isEdited?: boolean; // Flag to indicate if message was edited
  actionType?: string; // Track which quick action produced this message
  editHistory?: { // Track edit history
    timestamp: Date;
    previousContent: string;
  }[];
}

@Injectable({
  providedIn: 'root'
})
export class ChatHistoryService {
  // Separate chat histories for different interfaces
  private directChatHistorySubject = new BehaviorSubject<ChatMessage[]>([]);
  private documentChatHistorySubject = new BehaviorSubject<ChatMessage[]>([]);
  private academicChatHistorySubject = new BehaviorSubject<ChatMessage[]>([]);
  
  // Observable streams that components can subscribe to
  directChatHistory$ = this.directChatHistorySubject.asObservable();
  documentChatHistory$ = this.documentChatHistorySubject.asObservable();
  academicChatHistory$ = this.academicChatHistorySubject.asObservable();
  
  // Flags to track if histories have been cleared
  private directHistoryCleared = false;
  private documentHistoryCleared = false;
  private academicHistoryCleared = false;
  
  constructor(private aiService: AIService) {
    // Initialize from session storage if available
    this.loadHistoryFromSessionStorage();
  }
  
  // Direct chat methods
  getDirectChatHistory(): ChatMessage[] {
    return this.directChatHistorySubject.value;
  }
  
  addDirectChatMessage(message: ChatMessage): void {
    // Reset cleared flag when a new message is added
    this.directHistoryCleared = false;
    
    const messageWithId = {
      ...message,
      id: this.generateMessageId()
    };
    const currentHistory = [...this.directChatHistorySubject.value, messageWithId];
    this.directChatHistorySubject.next(currentHistory);
    this.saveHistoryToSessionStorage();
  }
  
  clearDirectChatHistory(): void {
    // Set flag to indicate history was manually cleared
    this.directHistoryCleared = true;
    
    this.directChatHistorySubject.next([]);
    this.saveHistoryToSessionStorage();
    // Also remove from session storage to prevent reloading
    sessionStorage.removeItem('directChatHistory');
  }
  
  // Document chat methods
  getDocumentChatHistory(): ChatMessage[] {
    return this.documentChatHistorySubject.value;
  }
  
  addDocumentChatMessage(message: ChatMessage): void {
    // Reset cleared flag when a new message is added
    this.documentHistoryCleared = false;
    
    const messageWithId = {
      ...message,
      id: this.generateMessageId()
    };
    const currentHistory = [...this.documentChatHistorySubject.value, messageWithId];
    this.documentChatHistorySubject.next(currentHistory);
    this.saveHistoryToSessionStorage();
  }
  
  clearDocumentChatHistory(): void {
    // Set flag to indicate history was manually cleared
    this.documentHistoryCleared = true;
    
    this.documentChatHistorySubject.next([]);
    // Also remove from session storage to prevent reloading
    sessionStorage.removeItem('documentChatHistory');
    this.saveHistoryToSessionStorage();
  }
  
  // Academic chat methods
  getAcademicChatHistory(): ChatMessage[] {
    return this.academicChatHistorySubject.value;
  }
  
  addAcademicChatMessage(message: ChatMessage): void {
    // Reset cleared flag when a new message is added
    this.academicHistoryCleared = false;
    
    const messageWithId = {
      ...message,
      id: this.generateMessageId()
    };
    const currentHistory = [...this.academicChatHistorySubject.value, messageWithId];
    this.academicChatHistorySubject.next(currentHistory);
    this.saveHistoryToSessionStorage();
  }
  
  clearAcademicChatHistory(): void {
    // Set flag to indicate history was manually cleared
    this.academicHistoryCleared = true;
    
    this.academicChatHistorySubject.next([]);
    // Also remove from session storage to prevent reloading
    sessionStorage.removeItem('academicChatHistory');
    this.saveHistoryToSessionStorage();
  }
  
  // Return status of history cleared flags
  isDirectHistoryCleared(): boolean {
    return this.directHistoryCleared;
  }
  
  isDocumentHistoryCleared(): boolean {
    return this.documentHistoryCleared;
  }
  
  isAcademicHistoryCleared(): boolean {
    return this.academicHistoryCleared;
  }
  
  // Message editing methods
  editDirectChatMessage(messageId: string, newContent: string): void {
    const currentHistory = this.directChatHistorySubject.value;
    const messageIndex = currentHistory.findIndex(msg => msg.id === messageId);
    
    if (messageIndex !== -1) {
      const message = currentHistory[messageIndex];
      const updatedMessage = {
        ...message,
        content: newContent,
        isEdited: true,
        editHistory: [
          ...(message.editHistory || []),
          {
            timestamp: new Date(),
            previousContent: message.content
          }
        ]
      };
      
      // Keep only messages up to and including the edited message
      const updatedHistory = currentHistory.slice(0, messageIndex + 1);
      updatedHistory[messageIndex] = updatedMessage;
      
      this.directChatHistorySubject.next(updatedHistory);
      this.saveHistoryToSessionStorage();
    }
  }

  editDocumentChatMessage(messageId: string, newContent: string): void {
    const currentHistory = this.documentChatHistorySubject.value;
    const messageIndex = currentHistory.findIndex(msg => msg.id === messageId);
    
    if (messageIndex !== -1) {
      const message = currentHistory[messageIndex];
      const updatedMessage = {
        ...message,
        content: newContent,
        isEdited: true,
        editHistory: [
          ...(message.editHistory || []),
          {
            timestamp: new Date(),
            previousContent: message.content
          }
        ]
      };
      
      // Keep only messages up to and including the edited message
      const updatedHistory = currentHistory.slice(0, messageIndex + 1);
      updatedHistory[messageIndex] = updatedMessage;
      
      this.documentChatHistorySubject.next(updatedHistory);
      this.saveHistoryToSessionStorage();
    }
  }

  // Helper method to generate unique message ID
  private generateMessageId(): string {
    return 'msg_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }
  
  // Storage methods
  private saveHistoryToSessionStorage(): void {
    // Only save if history hasn't been manually cleared
    try {
      if (!this.directHistoryCleared) {
        sessionStorage.setItem('directChatHistory', JSON.stringify(this.directChatHistorySubject.value));
      }
      if (!this.documentHistoryCleared) {
        sessionStorage.setItem('documentChatHistory', JSON.stringify(this.documentChatHistorySubject.value));
      }
      if (!this.academicHistoryCleared) {
        sessionStorage.setItem('academicChatHistory', JSON.stringify(this.academicChatHistorySubject.value));
      }
    } catch (error) {
      console.error('Error saving chat history to session storage:', error);
    }
  }
  
  private loadHistoryFromSessionStorage(): void {
    try {
      // Load direct chat history only if it hasn't been manually cleared
      if (!this.directHistoryCleared) {
        const savedDirectHistory = sessionStorage.getItem('directChatHistory');
        if (savedDirectHistory) {
          const parsedHistory = JSON.parse(savedDirectHistory);
          // Convert string timestamps back to Date objects
          const historyWithDateObjects = parsedHistory.map((msg: any) => ({
            ...msg,
            timestamp: new Date(msg.timestamp)
          }));
          this.directChatHistorySubject.next(historyWithDateObjects);
        }
      }
      
      // Load document chat history only if it hasn't been manually cleared
      if (!this.documentHistoryCleared) {
        const savedDocumentHistory = sessionStorage.getItem('documentChatHistory');
        if (savedDocumentHistory) {
          const parsedHistory = JSON.parse(savedDocumentHistory);
          // Convert string timestamps back to Date objects
          const historyWithDateObjects = parsedHistory.map((msg: any) => ({
            ...msg,
            timestamp: new Date(msg.timestamp)
          }));
          this.documentChatHistorySubject.next(historyWithDateObjects);
        }
      }
      
      // Load academic chat history only if it hasn't been manually cleared
      if (!this.academicHistoryCleared) {
        const savedAcademicHistory = sessionStorage.getItem('academicChatHistory');
        if (savedAcademicHistory) {
          const parsedHistory = JSON.parse(savedAcademicHistory);
          // Convert string timestamps back to Date objects
          const historyWithDateObjects = parsedHistory.map((msg: any) => ({
            ...msg,
            timestamp: new Date(msg.timestamp)
          }));
          this.academicChatHistorySubject.next(historyWithDateObjects);
        }
      }
    } catch (error) {
      console.error('Error loading chat history from session storage:', error);
    }
  }
  
  /**
   * Clear all chat histories (usually called on app refresh)
   */
  clearAllChatHistories(): void {
    this.clearDirectChatHistory();
    this.clearDocumentChatHistory();
    this.clearAcademicChatHistory();
    
    // Also clear from session storage 
    sessionStorage.removeItem('directChatHistory');
    sessionStorage.removeItem('documentChatHistory');
    sessionStorage.removeItem('academicChatHistory');
  }

  // Send a direct query and add results to chat history
  sendDirectQuery(message: string): Observable<string> {
    // Add the message to history
    this.addDirectChatMessage({
      role: 'user',
      content: message,
      timestamp: new Date(),
      id: this.generateMessageId()
    });

    // Call AI service to get response
    return this.aiService.generateTextForDirectQuery(message).pipe(
      map(response => {
        // Add the response to history
        this.addDirectChatMessage({
          role: 'assistant',
          content: response,
          timestamp: new Date(),
          id: this.generateMessageId()
        });

        return response;
      })
    );
  }

  setDocumentChatHistory(history: ChatMessage[]): void {
    this.documentChatHistorySubject.next(history);
    this.saveHistoryToSessionStorage();
  }
} 